<template>
    <div>
        <label :for="inputId" class="mb-2 block text-sm font-medium text-white">
            {{ label || $t('tag_name_input.label') }}
        </label>

        <div class="relative">
            <input
                :id="inputId"
                :value="modelValue"
                type="text"
                :required="required"
                maxlength="20"
                :placeholder="placeholder || $t('tag_name_input.placeholder')"
                data-testid="tag-name-input"
                :class="inputClass"
                @input="onInput"
            />

            <div
                v-if="status === 'checking'"
                class="absolute top-1/2 right-3 -translate-y-1/2 text-gray-400"
            >
                <svg class="h-5 w-5 animate-spin" viewBox="0 0 24 24" fill="none">
                    <!-- <circle
                        class="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        stroke-width="4"
                    /> -->
                    <path
                        class="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                    />
                </svg>
            </div>
            <div
                v-else-if="status === 'available'"
                class="absolute top-1/2 right-3 -translate-y-1/2 text-green-500"
            >
                <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M5 13l4 4L19 7"
                    />
                </svg>
            </div>
            <div
                v-else-if="status === 'taken'"
                class="absolute top-1/2 right-3 -translate-y-1/2 text-red-500"
            >
                <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M6 18L18 6M6 6l12 12"
                    />
                </svg>
            </div>
        </div>

        <div class="mt-1 flex items-center justify-between">
            <p class="text-xs" :class="statusMessageClass">{{ statusMessage }}</p>
            <p v-if="showCounter" class="text-xs text-gray-400">{{ modelValue.length }}/20</p>
        </div>
    </div>
</template>

<script>
import { apiService } from '@/services/api'

const DEBOUNCE_MS = 400

export default {
    name: 'TagNameInput',
    props: {
        modelValue: {
            type: String,
            default: '',
        },
        // The user's current name. Typing this exact value (case-insensitive)
        // is always considered available, so editing without changing the name
        // never reports "taken".
        currentName: {
            type: String,
            default: '',
        },
        label: {
            type: String,
            default: null,
        },
        placeholder: {
            type: String,
            default: null,
        },
        required: {
            type: Boolean,
            default: true,
        },
        showCounter: {
            type: Boolean,
            default: true,
        },
        inputClass: {
            type: String,
            default:
                'border-stroke-1 bg-card w-full border px-4 py-3 pr-10 text-white transition-colors focus:border-red-500 focus:outline-none',
        },
    },
    // status: 'idle' | 'checking' | 'available' | 'taken' | 'error'
    emits: ['update:modelValue', 'update:status'],
    data() {
        return {
            status: 'idle',
            inputId: `tag-name-${Math.random().toString(36).slice(2, 9)}`,
            debounceTimer: null,
        }
    },
    computed: {
        statusMessage() {
            switch (this.status) {
                case 'checking':
                    return this.$t('tag_name_input.checking')
                case 'available':
                    return this.$t('tag_name_input.available')
                case 'taken':
                    return this.$t('tag_name_input.taken')
                case 'error':
                    return this.$t('tag_name_input.error')
                default:
                    return ''
            }
        },
        statusMessageClass() {
            switch (this.status) {
                case 'available':
                    return 'text-green-500'
                case 'taken':
                case 'error':
                    return 'text-red-400'
                default:
                    return 'text-gray-400'
            }
        },
    },
    methods: {
        onInput(event) {
            const value = event.target.value
            this.$emit('update:modelValue', value)
            this.scheduleCheck(value)
        },

        scheduleCheck(value) {
            if (this.debounceTimer) {
                clearTimeout(this.debounceTimer)
            }

            const trimmed = value.trim()

            if (!trimmed) {
                this.setStatus('idle')
                return
            }

            if (trimmed.toLowerCase() === this.currentName.trim().toLowerCase()) {
                this.setStatus('available')
                return
            }

            this.setStatus('checking')
            this.debounceTimer = setTimeout(() => this.checkAvailability(trimmed), DEBOUNCE_MS)
        },

        async checkAvailability(value) {
            try {
                const response = await apiService.get(
                    `/api/v1/user/tag-name/available?tag_name=${encodeURIComponent(value)}`,
                )

                // Ignore stale responses if the field changed while we waited.
                if (value !== this.modelValue.trim()) {
                    return
                }

                this.setStatus(response?.available ? 'available' : 'taken')
            } catch (error) {
                console.error('Failed to check name availability:', error)
                this.setStatus('error')
            }
        },

        setStatus(status) {
            this.status = status
            this.$emit('update:status', status)
        },
    },
    beforeUnmount() {
        if (this.debounceTimer) {
            clearTimeout(this.debounceTimer)
        }
    },
}
</script>
